<template>
  <div>
    <form-section
      :title="$t('Automations_Title_BasicSettings')"
      :description="$t('Automations_Title_BasicSettings_Description')"
    >
      <el-row>
        <el-col :span="16">
          <rest-form-item
            prop="name"
            :label="$t('Automations_Field_Name')"
            :hint="$t('Automations_Field_Name_Hint')"
          >
            <el-input v-model="model.name" />
          </rest-form-item>
        </el-col>
      </el-row>
    </form-section>
    <rest-page
      :fetch-endpoint="fetchIntegrationsEndpoint"
      class="target-settings"
    >
      <form-section
        :title="$t('Automations_Title_Target')"
        :description="$t('Automations_Title_Target_Description')"
      >
        <el-row>
          <el-col :span="8">
            <rest-form-item
              :label="$t('Automations_Field_TargetType')"
              prop="target.type"
            >
              <el-select v-model="model.target.type">
                <el-option
                  v-for="type in availableTargetTypes"
                  :key="type"
                  :value="type"
                  :label="$t(TargetLabel[type])"
                />
              </el-select>
            </rest-form-item>
            <rest-form-item
              v-if="['fb_ads', 'google_ads'].includes(model.target.type)"
              prop="config.audience_id"
              :label="$t('Automations_Field_Audience')"
            >
              <el-select
                v-model="model.config.audience_id"
                :placeholder="$t('Automations_Field_Audience_Placeholder')"
                filterable
                remote
                :remote-method="fetchAudiences"
                @visible-change="fetchAudiences()"
                :loading="loading"
              >
                <el-option
                  v-for="item in audiencesOptions"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                >
                </el-option>
              </el-select>
            </rest-form-item>
          </el-col>

          <el-col :span="8" v-if="model.target.type === 'fs'">
            <rest-form-item
              :label="$t('Automations_Field_FileStorage')"
              prop="target.integration_id"
            >
              <el-select
                v-model="model.target.integration_id"
                :placeholder="$t('Automations_Field_FileStorage')"
              >
                <el-option
                  v-for="item in integrations"
                  :key="item.id"
                  :value="item.id"
                  :label="item.name"
                />
              </el-select>
            </rest-form-item>
          </el-col>
        </el-row>

        <el-row v-if="model.target.type === 'fs'">
          <el-col :span="8">
            <rest-form-item
              :label="$t('Automations_Field_FileDirectory')"
              :hint="$t('Automations_Field_FileDirectory_Hint')"
              prop="target.file_directory"
            >
              <el-input
                v-model="model.target.file_directory"
                :placeholder="$t('Automations_Field_FileDirectory_Placeholder')"
              />
            </rest-form-item>
          </el-col>

          <el-col :span="8">
            <rest-form-item
              :label="$t('Automations_Field_FileName')"
              :hint="$t('Automations_Field_FileName_Hint')"
              prop="target.file_name"
            >
              <el-input
                v-model="model.target.file_name"
                :placeholder="$t('Automations_Field_FileName_Placeholder')"
              />
            </rest-form-item>
          </el-col>
        </el-row>

        <el-row v-if="model.target.type === 'email'">
          <el-col :span="16">
            <rest-form-item
              prop="target.emails"
              :label="$t('Automations_Field_EmailsList')"
              :rules="emailValidation"
            >
              <el-select
                v-model="model.target.emails"
                multiple
                filterable
                allow-create
                default-first-option
                :placeholder="$t('Automations_Field_Emails')"
              />
            </rest-form-item>
          </el-col>
        </el-row>
      </form-section>
    </rest-page>
    <form-section
      :title="$t('Automations_Title_DataType')"
      :description="$t('Automations_Title_DataType_Description')"
    >
      <el-row>
        <el-col :span="8">
          <rest-form-item
            prop="config.identity"
            :label="$t('Automations_Field_Data')"
          >
            <el-select
              v-model="model.config.identity"
              @change="resetDeltaCheckbox"
            >
              <el-option
                v-for="option in identityOptions"
                :key="option.value"
                :label="$t(option.label)"
                :value="option.value"
              />
            </el-select>
          </rest-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col class="delta-container">
          <form-item>
            <checkbox
              v-model="model.config.delta"
              :title="$t('Automations_Title_Delta')"
              :description="$t('Automations_Title_Delta_Description')"
              :disabled="isDeltaOptionDisabled"
            />
          </form-item>
          <div class="tooltip">
            <el-tooltip
              class="box-item"
              effect="dark"
              :content="$t('Automations_Title_Delta_Tooltip')"
              placement="left"
              offset="7"
            >
              <a href="https://docs.xtremepush.com/docs" target="_blank">
                <i class="el-icon-info" style="font-size: 2rem;"></i>
              </a>
            </el-tooltip>
          </div>
        </el-col>
      </el-row>
    </form-section>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import { projectEndpoint } from '@/core/helpers'
import { TargetLabel } from '@/modules/automations/enum/TargetEnum'
import { mapGetters } from 'vuex'
import RestPage from '@/common/components/rest/RestPage'
import RestApi from '@/core/services/RestApi'
import { AudienceTypes } from '@/modules/automations/enum/ExternalAudienceTypeEnum'
import { $hasFeature } from '@/core/utils/VueAcl'
import FormItem from '@/common/components/form/FormItem'
import Checkbox from '@/common/components/form/Checkbox'
export default {
  name: 'StepExportDataSource',
  components: {
    Checkbox,
    FormItem,
    RestPage,
    RestFormItem,
    FormSection
  },
  inject: ['restForm'],
  props: {
    currentStep: {
      type: Object,
      required: true
    },
    steps: {
      type: Array,
      required: true
    },
    model: {
      default: null
    }
  },
  data() {
    return {
      integrations: [],
      loading: false,
      audiencesOptions: [],
      TargetLabel,
      emailValidation: {
        validator: this.validateEmail
      }
    }
  },
  mounted() {
    this.fetchAudiences()
  },
  computed: {
    ...mapGetters(['isGoogleAdsAvailable', 'isMetaAvailable']),
    audienceType() {
      switch (this.model.target.type) {
        case 'fb_ads':
          return AudienceTypes.meta
        case 'google_ads':
          return AudienceTypes.google
        default:
          return null
      }
    },
    identityOptions() {
      let options = [{ value: 'profile', label: 'Audiences_Type_Profiles' }]
      if (!['fb_ads', 'google_ads'].includes(this.model.target.type)) {
        return [
          ...options,
          ...[
            { value: 'device', label: 'Audiences_Type_Devices' },
            { value: 'campaign', label: 'Audiences_Type_CampaignEngagement' },
            { value: 'consent', label: 'Audiences_Type_Consent' },
            { value: 'otp', label: 'Audiences_Type_OTP' }
          ].filter(
            option =>
              this.isTypeVisible(option.value) ||
              this.model.config.identity === option.value
          )
        ]
      }
      return options
    },
    fetchIntegrationsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `integrations`,
        params: {
          type: 'sftp,gcs,s3',
          active: 1
        },
        handler: data => {
          this.integrations = data
        }
      })
    },
    availableTargetTypes() {
      return Object.keys(this.TargetLabel).filter(target => {
        if (target === 'fb_ads') {
          return this.isMetaAvailable && this.$store.state.app.features.meta
        }
        if (target === 'google_ads') {
          return (
            this.isGoogleAdsAvailable &&
            this.$store.state.app.features.googleads
          )
        }
        return true
      })
    },
    isDeltaOptionDisabled() {
      return (
        ['campaign'].includes(this.model.config.identity) ||
        ['google_ads', 'fb_ads'].includes(this.model.target.type)
      )
    }
  },
  methods: {
    isTypeVisible(type) {
      switch (type) {
        case 'campaign':
          return $hasFeature('campaignEngagementDeltaReport')
        case 'consent':
          return $hasFeature('subscriptionPreferences')
        case 'otp':
          return $hasFeature('otp')
        default:
          return true
      }
    },
    fetchAudiences(queryString) {
      this.loading = true
      return RestApi(
        projectEndpoint({
          method: 'get',
          url: `externalaudience`,
          params: {
            sort: 'name',
            name: queryString,
            audience_type: this.audienceType,
            is_deleted: 0
          }
        })
      )
        .then(({ data }) => {
          this.audiencesOptions = data
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    resetDeltaCheckbox() {
      this.model.config.delta = 0
    },
    validateEmail(rule, value, callback) {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      let emails = Array.isArray(value) ? value : [value]
      let invalidEmail = emails.find(email => !emailPattern.test(email))

      if (invalidEmail || !value || value.length === 0) {
        return callback(
          new Error(this.$t('Automations_Settings_ExportEmailValidation'))
        )
      } else {
        this.restForm.errors = this.restForm.errors.filter(
          error => error.field !== 'target.emails'
        )
      }
      callback()
    }
  },
  watch: {
    model: {
      handler() {
        if (['google_ads', 'fb_ads'].includes(this.model.target.type)) {
          this.model.config.delta = 0
        }
        if (this.model.config.identity === 'campaign') {
          this.model.config.delta = 1
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.target-settings {
  background-color: #fff;
}
.delta-container {
  display: flex;
  justify-content: space-between;
}
.tooltip {
  padding-top: 7px;
}
</style>



// WEBPACK FOOTER //
// src/modules/automations/components/StepExportDataSource.vue